import { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Copy, Loader2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/cn";
import {
  type ApiKeyScope,
  useApiKeys,
  useApiKeyUsage,
  useCreateApiKey,
  useRevokeApiKey,
} from "@/hooks/api";

const PAGE_SIZE = 50;

const SCOPES: Record<ApiKeyScope, { label: string; option: string }> = {
  "crm-lookup": { label: "CRM-oppslag", option: "CRM-oppslag: finner navngitte kunder" },
  analytics: { label: "Analyse", option: "Analyse: bare aggregerte tall" },
};

type ApiKey = NonNullable<ReturnType<typeof useApiKeys>["data"]>[number];

// t.Date is typed `unknown` in the generated client; the wire value is an ISO string.
function formatDate(date: unknown) {
  if (!date) return "—";
  return new Date(date as string).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function keyStatus(key: ApiKey) {
  if (key.revokedAt) return { label: "Tilbakekalt", variant: "secondary" as const };
  if (new Date(key.expiresAt as string) <= new Date()) return { label: "Utløpt", variant: "secondary" as const };
  return { label: "Aktiv", variant: "default" as const };
}

export function ApiKeysTab() {
  const { data: keys, isLoading, error } = useApiKeys();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const selected = keys?.find((k) => k.id === selectedId) ?? null;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">
          API-nøkler {keys && <span className="text-admin-text-subtle">({keys.length})</span>}
        </h3>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-3 w-3 mr-1" /> Ny
        </Button>
      </div>
      <p className="text-sm text-admin-text-secondary">
        En nøkkel gir lesetilgang til enten CRM-oppslag (abonnement, betalinger, labbesøk) eller
        analyse-API-et (bare aggregerte tall), og slutter å virke 30 dager etter at den ble laget.
      </p>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <p className="py-8 text-center text-sm text-admin-negative">
              Kunne ikke hente API-nøkler: {error.message}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Navn</TableHead>
                  <TableHead>Tilgang</TableHead>
                  <TableHead>Nøkkel</TableHead>
                  <TableHead>Laget av</TableHead>
                  <TableHead>Utløper</TableHead>
                  <TableHead>Sist brukt</TableHead>
                  <TableHead className="text-right">Kall</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(keys ?? []).map((key) => {
                  const status = keyStatus(key);
                  return (
                    <TableRow
                      key={key.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedId(key.id === selectedId ? null : key.id);
                        }
                      }}
                      className={cn(
                        "cursor-pointer hover:bg-admin-slate-hover",
                        key.id === selectedId && "bg-admin-card-hover",
                      )}
                      onClick={() => setSelectedId(key.id === selectedId ? null : key.id)}
                    >
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell>{SCOPES[key.scope].label}</TableCell>
                      <TableCell className="font-mono text-xs">{key.keyPrefix}…</TableCell>
                      <TableCell>{key.createdByName ?? "—"}</TableCell>
                      <TableCell>{formatDate(key.expiresAt)}</TableCell>
                      <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                      <TableCell className="text-right">{key.usageCount}</TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {status.label === "Aktiv" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRevoking(key);
                            }}
                          >
                            Tilbakekall
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(keys ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-admin-text-secondary py-8">
                      Ingen API-nøkler
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selected && <UsageCard key={selected.id} apiKey={selected} />}

      <CreateKeyDialog open={creating} onClose={() => setCreating(false)} />
      <RevokeKeyDialog apiKey={revoking} onClose={() => setRevoking(null)} />
    </div>
  );
}

function UsageCard({ apiKey }: { apiKey: ApiKey }) {
  const [page, setPage] = useState(0);
  const usage = useApiKeyUsage(apiKey.id, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
  const rows = usage.data ?? [];

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <h4 className="text-sm font-semibold">Bruk av «{apiKey.name}»</h4>
        {usage.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : usage.error ? (
          <p className="py-8 text-center text-sm text-admin-negative">
            Kunne ikke hente bruken: {usage.error.message}
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tidspunkt</TableHead>
                  <TableHead>Endepunkt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Treff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell className="font-mono text-xs">{row.route}</TableCell>
                    <TableCell>
                      <Badge variant={row.status < 400 ? "default" : "destructive"}>{row.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{row.resultCount ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-admin-text-secondary py-8">
                      Ikke brukt ennå
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between">
              <p className="text-xs text-admin-text-secondary">Side {page + 1}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rows.length < PAGE_SIZE}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CreateKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createKey = useCreateApiKey();
  const [name, setName] = useState("");
  // No default: which API a key opens is chosen every time.
  const [scope, setScope] = useState<ApiKeyScope | "">("");
  const [key, setKey] = useState<string | null>(null);
  const ready = !!name.trim() && !!scope;

  const close = () => {
    setName("");
    setScope("");
    setKey(null);
    createKey.reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!scope) return;
    try {
      const created = await createKey.mutateAsync({ name: name.trim(), scope });
      setKey(created.key);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunne ikke lage nøkkel");
    }
  };

  const copy = async () => {
    if (!key) return;
    await navigator.clipboard.writeText(key);
    toast.success("Nøkkelen er kopiert");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A shown key closes only through "Ferdig", so it is not lost to a stray click.
        if (!next && !createKey.isPending && !key) close();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ny API-nøkkel</DialogTitle>
        </DialogHeader>
        {key ? (
          <div className="mt-2 space-y-3 text-sm">
            <p className="text-admin-text-secondary">
              Kopier nøkkelen nå. Den vises bare én gang og slutter å virke om 30 dager.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={key} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button variant="outline" size="sm" onClick={copy}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={close}>
                Ferdig
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="mt-2 space-y-3 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) handleCreate();
            }}
          >
            <div className="space-y-1">
              <label htmlFor="api-key-name" className="text-xs font-medium text-admin-text-secondary">
                Navn (hva nøkkelen brukes til)
              </label>
              <Input id="api-key-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1">
              <label htmlFor="api-key-scope" className="text-xs font-medium text-admin-text-secondary">
                Tilgang
              </label>
              <Select
                id="api-key-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value as ApiKeyScope | "")}
              >
                <option value="" disabled>
                  Velg hva nøkkelen skal åpne
                </option>
                {Object.entries(SCOPES).map(([value, { option }]) => (
                  <option key={value} value={value}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={close} disabled={createKey.isPending}>
                Avbryt
              </Button>
              <Button type="submit" size="sm" disabled={!ready || createKey.isPending}>
                {createKey.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Lag nøkkel
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RevokeKeyDialog({ apiKey, onClose }: { apiKey: ApiKey | null; onClose: () => void }) {
  const revokeKey = useRevokeApiKey();

  const handleRevoke = async () => {
    if (!apiKey) return;
    try {
      await revokeKey.mutateAsync(apiKey.id);
      toast.success("Nøkkelen er tilbakekalt");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunne ikke tilbakekalle nøkkelen");
    }
  };

  return (
    <Dialog
      open={apiKey !== null}
      onOpenChange={(open) => {
        if (!open && !revokeKey.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Tilbakekall API-nøkkel</DialogTitle>
        </DialogHeader>
        {apiKey && (
          <p className="mt-2 text-sm text-admin-text-secondary">
            «{apiKey.name}» ({apiKey.keyPrefix}…) slutter å virke med en gang. Dette kan ikke angres.
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={revokeKey.isPending}>
            Avbryt
          </Button>
          <Button variant="destructive" size="sm" onClick={handleRevoke} disabled={revokeKey.isPending}>
            {revokeKey.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            Tilbakekall
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
